import { PieChartOutlined, SettingOutlined, TableOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { useSuspenseQuery } from '@apollo/client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartMixed } from '@fortawesome/sharp-solid-svg-icons'
import { FetchKpiChartDisplaySettingPresetsDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { POSTHOG_EVENTS, posthogTrackView } from '@salescore/client-base'
import { ClosableResizableSider, useCanForView, useMe } from '@salescore/client-common'
import {
  type ChartAggregation,
  type CompiledKpiViewConfig,
  CORE_CONSTANT,
  getMetricFromKpiFormula,
  type ViewConfigKpiWithMetric,
  type ViewKpiAppearance,
} from '@salescore/core'
import type {
  ChartLabelDisplayModeValue,
  ChartLegendsDisplayModeValue,
  StackedChartType,
} from '@salescore/frontend-common'
import { Button, message } from 'antd'
import { Array, Order, pipe } from 'effect'
import { t } from 'i18next'
import { type Dispatch, type ReactElement, type SetStateAction, useCallback, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { usePivotColumns } from '../../hooks/usePivotColumns.tsx'
import {
  buildInitialKpiChartDisplaySettingPresetsState,
  checkChartDisplaySettingChanges,
  isChartFormat,
  type KpiChartDisplaySettingPresetsState,
  resolveOriginalViewId,
} from '../../kpi/kpiChartDisplaySettingPresets'
import { kpiParameterAtom } from '../../recoil/navigation/atoms'
import {
  kpiFormModifiedAtom,
  type SalescoreObjectAbility,
  type ViewAbility,
  viewsContextAtom,
} from '../../recoil/view/atoms'
import { useViewConfigKpi } from '../../recoil/view/hooks/useViewConfigKpi'
import { KpiForm } from '../../rsheet/components/footer/KpiForm'
import { ExpandableSider } from './ExpandableSider'
import { type PanelOption, VerticalPanelPicker } from './VerticalPanelPicker'
import { KpiParameterSider } from './ViewUIKpi/KpiParameterSider'
import { KpiTable, ViewUIKpiBody } from './ViewUIKpi/ViewUIKpiBody'
import { KpiPivotNavigation } from './ViewUIKpiPivot/KpiPivotNavigation'

export type KpiPieChartCategoryFilters = Array<{ key: string }>
export type SetKpiPieChartCategoryFilters = Dispatch<SetStateAction<KpiPieChartCategoryFilters>>

export interface ChartDesignSettings {
  chartType: StackedChartType
  chartValueLabelDisplayMode: ChartLabelDisplayModeValue
  chartValueLabelUnitType: ViewKpiAppearance['unitType']
  chartValueLabelDecimalPlaceType: ViewKpiAppearance['decimalPlaceType']
  chartValueLabelShouldShowPercentage: boolean
  chartAxisLabelDisplayMode: ChartLabelDisplayModeValue
  chartLegendsDisplayMode: ChartLegendsDisplayModeValue
}
export type SetChartDesignSettings = Dispatch<SetStateAction<ChartDesignSettings>>
export type SetChartAggregation = (aggregation: ChartAggregation) => void

export const DEFAULT_CHART_DESIGN_SETTINGS: ChartDesignSettings = {
  chartType: 'bar',
  chartValueLabelDisplayMode: 'auto',
  chartValueLabelUnitType: 'noUnit',
  chartValueLabelDecimalPlaceType: 'automatic',
  chartValueLabelShouldShowPercentage: false,
  chartAxisLabelDisplayMode: 'auto',
  chartLegendsDisplayMode: 'auto',
}

export const ViewUIKpi = ({
  compiledConfig,
  view,
  ability,
  hasChangeToKpiPivotParameter,
  setHasChangeToKpiPivotParameter,
  setHasChangeToViewSchema,
}: {
  compiledConfig: CompiledKpiViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  hasChangeToKpiPivotParameter: boolean
  setHasChangeToKpiPivotParameter: (hasChange: boolean) => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const [component] = compiledConfig.ui
  const me = useMe()
  const { format, sortedOptions, onClickPanel } = useKpiDisplayFormatPicker(view, ability)
  const { systemColumns, rowColumns, pivotColumns } = usePivotColumns(compiledConfig, view)
  const columns = [...systemColumns, ...rowColumns, ...pivotColumns]
  const kpiParameter = useRecoilValue(kpiParameterAtom)
  const originalViewId = resolveOriginalViewId(view.id)
  const { data } = useSuspenseQuery(FetchKpiChartDisplaySettingPresetsDocument, {
    variables: { organizationId: me.organization.id, viewId: originalViewId },
  })
  const presets = data.kpiChartDisplaySettingPresets
  const canUpdate = useCanForView('update', view)

  // 円グラフで使用するカテゴリのフィルター設定
  const [kpiPieChartCategoryFilters, setKpiPieChartCategoryFilters] = useState<KpiPieChartCategoryFilters>([])
  // format別の表示設定（デザイン + 集計軸 + 保存済み状態）
  // Apolloキャッシュがあれば初回レンダリングで正しく初期化される
  // キャッシュミス時はデフォルト設定で開始し、ユーザー操作で上書きされる
  const [kpiChartDisplaySettingPresetsState, setKpiChartDisplaySettingPresetsState] =
    useState<KpiChartDisplaySettingPresetsState>(() =>
      buildInitialKpiChartDisplaySettingPresetsState(presets, component.dimensions, kpiParameter.pivot.rows),
    )

  // setChartDesignSettingsはkpiChartDisplaySettingPresetsStateのdesignを更新するラッパー
  const setChartDesignSettings: SetChartDesignSettings = useCallback(
    (action) => {
      setKpiChartDisplaySettingPresetsState((old) => {
        if (!isChartFormat(format)) return old
        const entry = old[format]
        const current = entry.design
        const newDesign = typeof action === 'function' ? action(current) : action
        return { ...old, [format]: { ...entry, design: newDesign } }
      })
    },
    [format],
  )

  // setChartAggregationはkpiChartDisplaySettingPresetsStateのaggregationを更新するラッパー
  const setChartAggregation: SetChartAggregation = useCallback((aggregation) => {
    setKpiChartDisplaySettingPresetsState((old) => {
      const { chartFormat } = aggregation
      const entry = old[chartFormat]
      return {
        ...old,
        [chartFormat]: { ...entry, aggregation },
      }
    })
  }, [])

  // combo/pieパネルに未保存インジケーターを付与
  const optionsWithIndicator = sortedOptions.map((option) => {
    if (!isChartFormat(option.value)) return option
    const entry = kpiChartDisplaySettingPresetsState[option.value]
    return { ...option, showIndicator: checkChartDisplaySettingChanges(entry, canUpdate).canRevert }
  })

  if (view.config.type !== 'kpi') {
    // ありえないはず
    return <></>
  }

  if (view.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX)) {
    return (
      <ClosableResizableSider
        siderKey="ViewUIKpiSheet"
        // kpiの場合は常にvisibleにする
        defaultVisibility={false}
        defaultWidth={380}
        sider={({ close }) => (
          <KpiParameterSider
            component={component}
            format={format}
            close={close}
            kpiPieChartCategoryFilters={kpiPieChartCategoryFilters}
            setKpiPieChartCategoryFilters={setKpiPieChartCategoryFilters}
            setChartDesignSettings={setChartDesignSettings}
            kpiChartDisplaySettingPresetsState={kpiChartDisplaySettingPresetsState}
            setChartAggregation={setChartAggregation}
            view={view}
          />
        )}
        body={({ setVisibility, visibility }) => (
          <div className="flex h-full flex-col overflow-y-auto bg-white">
            <KpiPivotNavigation
              component={component}
              siderVisibility={visibility}
              setSiderVisibility={setVisibility}
              columns={columns}
              compiledConfig={compiledConfig}
              view={view}
              ability={ability}
              hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
              setHasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
              setHasChangeToViewSchema={setHasChangeToViewSchema}
            />
            <div
              className="min-h-0 flex-1 border"
              // ここでkey指定するとviewの切り替え時に全レンダリングが走って良くないが、実装ミスでレンダリングが微妙になるのを防ぐのを優先する
              key={view.id}
            >
              <KpiTable
                component={component}
                columns={columns}
                compiledConfig={compiledConfig}
                view={view}
                ability={ability}
                setHasChangeToViewSchema={setHasChangeToViewSchema}
              />
            </div>
          </div>
        )}
      />
    )
  }

  return (
    <div className="size-full">
      <VerticalPanelPicker
        options={optionsWithIndicator}
        selectedValue={format}
        onClick={(option) => {
          void onClickPanel(option)
        }}
        body={
          <div className="h-full">
            {format === 'sheet' ? (
              <ViewUIKpiBody
                component={component}
                format={format}
                kpiPieChartCategoryFilters={kpiPieChartCategoryFilters}
                kpiChartDisplaySettingPresetsState={kpiChartDisplaySettingPresetsState}
                columns={columns}
                compiledConfig={compiledConfig}
                view={view}
                ability={ability}
                setHasChangeToViewSchema={setHasChangeToViewSchema}
              />
            ) : // eslint-disable-next-line sonarjs/no-nested-conditional
            format === 'setting' ? (
              <div className="mb-16 h-full overflow-y-hidden bg-white">
                <KpiForm view={view} ability={ability} />
              </div>
            ) : (
              <ExpandableSider
                // kpiの場合は常にvisibleにする
                visibility={true}
                width={380}
                sider={
                  <KpiParameterSider
                    component={component}
                    format={format}
                    kpiPieChartCategoryFilters={kpiPieChartCategoryFilters}
                    setKpiPieChartCategoryFilters={setKpiPieChartCategoryFilters}
                    setChartDesignSettings={setChartDesignSettings}
                    kpiChartDisplaySettingPresetsState={kpiChartDisplaySettingPresetsState}
                    setChartAggregation={setChartAggregation}
                    view={view}
                  />
                }
                body={
                  <div className="flex h-full flex-col overflow-y-auto bg-white">
                    <KpiPivotNavigation
                      component={component}
                      columns={columns}
                      compiledConfig={compiledConfig}
                      chartDisplaySettingProps={
                        format === 'combo' || format === 'pie'
                          ? {
                              format,
                              kpiChartDisplaySettingPresetsState,
                              setKpiChartDisplaySettingPresetsState,
                              viewId: view.id,
                            }
                          : undefined
                      }
                      view={view}
                      ability={ability}
                      hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
                      setHasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
                      setHasChangeToViewSchema={setHasChangeToViewSchema}
                    />
                    <div
                      className="min-h-0 flex-1 border"
                      // ここでkey指定するとviewの切り替え時に全レンダリングが走って良くないが、実装ミスでレンダリングが微妙になるのを防ぐのを優先する
                      key={view.id}
                    >
                      <ViewUIKpiBody
                        component={component}
                        format={format}
                        kpiPieChartCategoryFilters={kpiPieChartCategoryFilters}
                        kpiChartDisplaySettingPresetsState={kpiChartDisplaySettingPresetsState}
                        columns={columns}
                        compiledConfig={compiledConfig}
                        view={view}
                        ability={ability}
                        setHasChangeToViewSchema={setHasChangeToViewSchema}
                      />
                    </div>
                  </div>
                }
              />
            )}
          </div>
        }
      />
    </div>
  )
}

export type KpiDisplayFormat = 'table' | 'combo' | 'pie' | 'sheet' | 'setting'

type KpiDisplayFormatOption = PanelOption & { value: KpiDisplayFormat }

const useKpiDisplayFormatPicker = (view: ViewFieldsFragment, ability: ViewAbility & SalescoreObjectAbility) => {
  const [config] = useViewConfigKpi()
  const context = useRecoilValue(viewsContextAtom)
  const [formModified, setFormModified] = useRecoilState(kpiFormModifiedAtom)
  // 必要な設定が終わっていなければ、設定画面以外を表示しない
  const isSettingCompleted = checkSettingCompleted(config)
  const { hasChanges, isSaveConfigButtonActive, setIsSaveConfigButtonActive } = useRecoilValue(viewsContextAtom)

  const options: KpiDisplayFormatOption[] = [
    {
      value: 'setting',
      label: t(`KPI設定`),
      icon: <SettingOutlined className="" style={{ fontSize: 26 }} />,
      disabled:
        !ability.canDisplayConfigEdit ||
        context.prevView?.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX) === true,
    },
    {
      value: 'sheet',
      label: t(`一覧`),
      icon: <UnorderedListOutlined className="" style={{ fontSize: 26 }} />,
      disabled: !isSettingCompleted,
    },
    {
      value: 'pie',
      label: t(`円グラフ`),
      icon: <PieChartOutlined className="" style={{ fontSize: 26 }} />,
      disabled: !isSettingCompleted,
      posthogEventName: POSTHOG_EVENTS.view_kpi_pie_chart_panel,
    },
    {
      value: 'table',
      label: t(`ピボット`),
      icon: <TableOutlined className="" style={{ fontSize: 26 }} />,
      disabled: !isSettingCompleted,
      posthogEventName: POSTHOG_EVENTS.view_kpi_pivot_table_panel,
    },
    {
      value: 'combo',
      label: t(`グラフ`),
      icon: <FontAwesomeIcon icon={faChartMixed} style={{ fontSize: 26 }} />,
      disabled: !isSettingCompleted,
      posthogEventName: POSTHOG_EVENTS.view_kpi_bar_chart_panel,
    },
  ]

  // ドリルダウンしてKPIを表示したときは一覧が一番上、そうでない時はKPI設定が上
  const sortedOptions =
    context.prevView === undefined
      ? options
      : pipe(options, Array.sortBy(Order.mapInput(Order.Number, (x) => (x.value === 'setting' ? 1 : 0))))

  const [format, setFormat] = useState<KpiDisplayFormat>(sortedOptions.at(0)?.value ?? 'setting')

  const onClickPanel = async (option: KpiDisplayFormatOption): Promise<void> => {
    if (
      formModified || // KPI設定パネルで変更があるとき
      (context.prevView?.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX) !== true && hasChanges === true) || // KPIシートのレコードに変更があるとき(ただし KPI プレビューのセルをクリックした時を除く)
      isSaveConfigButtonActive === true // KPIシートの設定に変更があるとき
    ) {
      const messageKey = `move-panel-warning`
      await message.warning({
        key: messageKey,
        content: (
          <span>
            {t(`変更差分がある場合はタブを切り替えられません。`)}
            <br />
            {t(`差分を削除してタブを切り替えますか？`)}
            <Button
              className="ml-2"
              onClick={() => {
                setFormModified(false)
                setIsSaveConfigButtonActive?.(false)
                setFormat(option.value)
                message.destroy(messageKey)
                if (option.posthogEventName !== undefined) {
                  posthogTrackView(option.posthogEventName, {
                    viewId: view.id,
                    prevViewId: context.prevView?.id,
                    prevViewType: context.prevView?.type,
                    nodeName: undefined,
                  })
                }
              }}
            >
              {t(`はい`)}
            </Button>
          </span>
        ),
        duration: 3,
      })
      return
    }
    setFormat(option.value)
    if (option.posthogEventName !== undefined) {
      posthogTrackView(option.posthogEventName, {
        viewId: view.id,
        prevViewId: context.prevView?.id,
        prevViewType: context.prevView?.type,
        nodeName: undefined,
      })
    }
  }

  return {
    format,
    sortedOptions,
    onClickPanel,
  }
}

function checkSettingCompleted(config: ViewConfigKpiWithMetric | undefined): boolean {
  if (config === undefined) return false
  return getMetricFromKpiFormula(config.formula) !== undefined
}
